import React, { useEffect, useState } from 'react'
import { 
  Box, 
  Drawer, 
  Typography, 
  List, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  IconButton,
  Button
} from '@mui/material'
import { styled } from '@mui/material/styles'
import moment from 'moment'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import { makeStyles } from '@mui/styles'

import { useVersionHistory } from '../../hooks/useVersionHistory'
import { useSidebarParam } from '../../hooks/useSidebarParam'
import { eventDetail } from '../../services/EventService'
import { SIDEBAR_DRAWER_WIDTH, closeSidebarParam } from '../../utils/sidebarUrl'
import { VersionHistoryStatus } from '../../services/Types'

const STATUS_LABELS = {
  [VersionHistoryStatus.SAVED]: 'Unsubmitted',
  [VersionHistoryStatus.REJECTED]: 'Rejected - Unsubmitted',
  [VersionHistoryStatus.APPROVAL1]: 'Pending',
  [VersionHistoryStatus.APPROVAL2]: 'Approval Level 1',
  [VersionHistoryStatus.APPROVAL3]: 'Approval Level 2',
  [VersionHistoryStatus.APPROVED]: 'Approved',
  [VersionHistoryStatus.BLOCKED]: 'Blocked',
  [VersionHistoryStatus.RELEASED]: 'Released',
  [VersionHistoryStatus.UNLOCKED]: 'Unlocked',
  [VersionHistoryStatus.CANCELLED]: 'Cancelled'
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: SIDEBAR_DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: SIDEBAR_DRAWER_WIDTH,
    boxSizing: 'border-box',
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
}))

const useStyles = makeStyles({
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: '#f5f5f5'
  },
  drawerContent: {
    padding: '16px',
    height: 'calc(100vh - 80px)',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'inherit'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#D9D9D9',
      borderRadius: '1.25rem'
    }
  },
  accordionItem: {
    marginBottom: '8px',
    '&:before': {
      display: 'none',
    }
  },
  accordionSummary: {
    backgroundColor: '#f8f9fa',
    '&:hover': {
      backgroundColor: '#e9ecef'
    }
  },
  changeItem: {
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  currentVersion: {
    backgroundColor: '#e3f2fd',
    borderLeft: '4px solid #006BD7'
  }
})

export const VersionHistoryDrawer: React.FC = () => {
  const classes = useStyles()
  const { isOpen, eventId } = useSidebarParam('history')
  const [eventData, setEventData] = useState<any>({})
  const [showDetails, setShowDetails] = useState(false)

  const { versionHistory } = useVersionHistory(eventId)

  useEffect(() => {
    if (!eventId) return

    const loadEventData = async () => {
      try {
        const { data } = await eventDetail(eventId)
        if (data?.eventDetail?.statusCode === 200) {
          setEventData(data.eventDetail.data)
        }
      } catch (error) {
        console.log(error)
      }
    }

    loadEventData()
  }, [eventId])

  const handleClose = () => {
    closeSidebarParam('history')
  }

  const getStatusStyle = (status: string) => {
    let color = ''
    if (status === 'Approval Level 1') color = '#FF5722'
    else if (status === 'Approval Level 2') color = '#0288D1'
    else if (status === 'Pending') color = '#6200EA'
    else if (status === 'Unsubmitted') color = '#455A64'
    else if (status === 'Approved') color = '#009A44'
    else if (status === 'Rejected - Unsubmitted') color = '#D50000'
    else if (status === 'Released') color = '#D50000'
    else if (status === 'Unlocked') color = '#D50000'

    const getIconBgColor = () => {
      const opacity = 0.12
      const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255)
      return color + _opacity.toString(16).toUpperCase()
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            height: '1.5rem',
            width: '1.5rem',
            borderRadius: '1.5rem',
            bgcolor: getIconBgColor,
            mr: 1,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {status === 'Unsubmitted' || status === 'Pending' ? (
            <QuestionMarkIcon
              sx={{ fontSize: '12px', color: color }}
            />
          ) : status === 'Rejected - Unsubmitted' ? (
            <PriorityHighIcon
              sx={{ fontSize: '12px', color: color }}
            />
          ) : (
            <CheckIcon sx={{ fontSize: '12px', color: color }} />
          )}
        </Box>
        <Typography sx={{ fontWeight: 400, fontSize: '12px' }} color={color}>
          {status}
        </Typography>
      </Box>
    )
  }

  if (!isOpen) return null

  return (
    <StyledDrawer
      variant="persistent"
      anchor="right"
      open={isOpen}
      sx={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100vh',
        zIndex: 1200
      }}
    >
      <Box className={classes.drawerHeader}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Event History
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.drawerContent}>
        {eventData.title && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#79747E',
                fontWeight: 500 
              }}
            >
              {eventData.title}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowDetails(!showDetails)}
              sx={{ 
                minWidth: 'auto', 
                px: 1, 
                py: 0.5,
                fontSize: '0.75rem',
                color: 'text.secondary',
                textTransform: 'none',
                backgroundColor: '#e9ecef',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>
          </Box>
        )}

        <List>
          {versionHistory.map((revision, index) => (
            <Accordion 
              key={revision.id} 
              className={classes.accordionItem}
              sx={index === 0 ? { 
                backgroundColor: '#e3f2fd',
                borderLeft: '4px solid #006BD7',
                '& .MuiAccordionSummary-root': {
                  backgroundColor: '#e3f2fd'
                }
              } : {}}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {moment(Number(revision.createdAt)).format('MM/DD/YYYY hh:mm A')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    by {revision.user.firstName} {revision.user.lastName}
                  </Typography>
                  {getStatusStyle(STATUS_LABELS[revision.status as VersionHistoryStatus])}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {revision.changes && revision.changes.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Changes:
                    </Typography>
                    {revision.changes.map((change, changeIndex) => (
                      <Box key={changeIndex} className={classes.changeItem}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {change.field}
                        </Typography>
                        {showDetails && (
                          <Typography variant="body2" color="text.secondary">
                            {change.description}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No changes recorded
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </List>

        {versionHistory.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No version history available
          </Typography>
        )}
      </Box>
    </StyledDrawer>
  )
} 